import * as React from 'react';

import { IIcon, Icon } from '@rbilabs/components-library';
import { Link } from 'react-router';
import styled from 'styled-components';

import { useCdpContext } from 'state/cdp';
import { CustomEventNames } from 'state/cdp/constants';
import { useFlag } from 'state/launchdarkly';
import { isExternalLink } from 'utils/is-external-link';
import { LaunchDarklyFlag } from 'utils/launchdarkly';

export const StyledLink = styled(Link)`
  border-block-end: 1px solid ${Styles.color.grey.four};
  width: 100%;
  font-family: ${Styles.fontFamily.body};
  text-decoration: none;
  color: ${p => p.theme.token('text-default')};
  padding: 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const StyledLabel = styled.label`
  margin: 0;
  width: 100%;
`;

export const StyledIcon = styled.div`
  margin-block: 0;
  margin-inline-start: 0;
  margin-inline-end: 0.8rem;
`;

interface IArrowLinksProps<T extends Element = Element> {
  prefixIcon?: keyof IIcon;
  eventName?: (typeof CustomEventNames)[keyof typeof CustomEventNames];
  to: string;
  onClick?: (event: React.MouseEvent<T>) => void;
}

export const ArrowLink: React.FC<
  React.ComponentProps<typeof StyledLink | typeof StyledLabel | typeof StyledIcon> &
    IArrowLinksProps
> = ({ children, eventName, onClick, prefixIcon, ...props }) => {
  const cdp = useCdpContext();

  const enableAccountMenuBrandIcons = useFlag(LaunchDarklyFlag.ENABLE_ACCOUNT_MENU_BRAND_ICONS);

  const handleClick = (e: React.MouseEvent) => {
    if (eventName) {
      cdp.logNavigationClick(eventName);
    }

    if (onClick) {
      onClick(e);
    }

    return true;
  };

  return isExternalLink(props.to) ? (
    <StyledLink to={props.to} target="_blank" onClick={handleClick}>
      {children}
      <Icon
        icon="next"
        color="icon-default"
        width="16px"
        aria-hidden
        data-testid="icon-external-link-test-id"
      />
    </StyledLink>
  ) : (
    <StyledLink {...(props as IArrowLinksProps)} onClick={handleClick}>
      {enableAccountMenuBrandIcons && prefixIcon && (
        <StyledIcon>
          <Icon
            icon={prefixIcon}
            color="icon-default"
            aria-hidden
            width="24px"
            height="24px"
            data-testid={`${prefixIcon}-prefix-icon-test-id`}
          />
        </StyledIcon>
      )}
      <StyledLabel>{children}</StyledLabel>
      <Icon icon="next" color="icon-default" width="16px" aria-hidden data-testid="icon-test-id" />
    </StyledLink>
  );
};
